<template>
  <header id="top" class="fixed-top shadow-sm bg-white border-bottom">
    <the-impersonate-stop />
    <div class="container-xl">
      <div class="d-flex align-items-center justify-content-between py-8" style="min-height: 52px;">
        <div class="d-flex align-items-center">
          <div class="d-flex flex-column align-items-start me-32">
            <a class="d-flex align-items-center text-decoration-none" style="cursor: pointer;" @click.prevent="$router.push({ name: 'supplierPortal.upcoming' })">
              <img alt="WAW Logo" src="https://site.waw.travel/images/wawlogo.svg" style="height: 32px; width: auto;"/>
            </a>
          </div>
          
          <nav class="d-none d-md-flex align-items-center gap-4 me-32">
            <router-link 
              :to="{ name: 'supplierPortal.upcoming', query: $route.query }" 
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all"
              :class="$route.name === 'supplierPortal.upcoming' ? 'bg-primary text-white shadow-sm' : 'text-muted'"
            >
              {{ $t('supplier.portal.upcoming') }}
            </router-link>
            <router-link 
              :to="{ name: 'supplierPortal.past', query: $route.query }" 
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all"
              :class="$route.name === 'supplierPortal.past' ? 'bg-primary text-white shadow-sm' : 'text-muted'"
            >
              {{ $t('supplier.portal.past') }}
            </router-link>
            <router-link 
              v-if="isAdmin"
              :to="{ name: 'supplier.index' }" 
              class="nav-link px-8 py-4 rounded-3 fw-bold text-sm transition-all text-muted"
            >
              <v-icon icon="arrow-left" size="xs" class="me-4" />
              Back to Admin
            </router-link>
          </nav>
        </div>

        <div class="d-flex align-items-center gap-20">
          <div class="d-none d-md-flex flex-column align-items-end me-8">
            <span class="badge bg-primary-soft text-primary rounded-pill px-12 py-6 fw-bold text-uppercase letter-spacing-wider" style="font-size: 0.65rem;">
              Supplier Portal
            </span>
          </div>
          
          <div class="dropdown">
            <button class="btn btn-icon rounded-circle shadow-none p-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <v-icon icon="user" size="xl" />
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow border-0 mt-8 py-8">
              <li class="px-16 py-8 border-bottom mb-8">
                <div v-if="supplierName" class="fw-bold text-dark text-sm">{{ supplierName }}</div>
                <div class="text-xs text-muted">{{ user?.email }}</div>
                <div class="text-xs text-muted">{{ user?.name }}</div>
              </li>
              <li class="d-md-none">
                <router-link :to="{ name: 'supplierPortal.upcoming', query: $route.query }" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="calendar" size="sm" class="me-12" />
                  <span>{{ $t('supplier.portal.upcoming') }}</span>
                </router-link>
              </li>
              <li class="d-md-none">
                <router-link :to="{ name: 'supplierPortal.past', query: $route.query }" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="calendar" size="sm" class="me-12" />
                  <span>{{ $t('supplier.portal.past') }}</span>
                </router-link>
              </li>
              <li v-if="isAdmin" class="d-md-none">
                <router-link :to="{ name: 'supplier.index' }" class="dropdown-item d-flex align-items-center py-8 px-16">
                  <v-icon icon="arrow-left" size="sm" class="me-12" />
                  <span>Back to Admin</span>
                </router-link>
              </li>
              <li class="d-md-none border-bottom my-8"></li>
              <li>
                <a class="dropdown-item d-flex align-items-center py-8 px-16 text-danger" href="#" @click.prevent="logout">
                  <v-icon icon="logout" size="sm" class="me-12" />
                  <span>{{ $t('general.menu.logout') }}</span>
                </a>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import TheImpersonateStop from '@/components/header/TheImpersonateStop.vue';

import api from '@/api';

export default defineComponent({
  name: 'TheSupplierHeader',
  components: {
    VIcon,
    TheImpersonateStop,
  },
  computed: {
    ...mapState(useAuthStore, ['user', 'agency']),
    supplierName() {
      if (this.agency?.name) return this.agency.name;
      
      return (this.user as any)?.supplier?.name || '';
    },
    isAdmin() {
      return (this.user as any)?.role === 'admin' || (this.user as any)?.roles?.some((r: any) => r.name === 'admin');
    }
  },
  methods: {
    async logout() {
      const authStore = useAuthStore();
      await authStore.logout();
      this.$router.push({ name: 'login' });
    },
  },
  async mounted() {
    // If agency is missing but we have a supplierId, try to fetch the supplier
    if (!this.agency && this.user?.supplierId) {
      try {
        const supplier = await api.supplier.retrieve(this.user.supplierId);
        if (supplier) {
          const authStore = useAuthStore();
          authStore.setAgency(supplier as any);
        }
      } catch (error) {
        console.error('Failed to fetch supplier info:', error);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
#top {
  background: white;
  z-index: 1030;
}

.bg-primary-soft { background-color: rgba(13, 110, 253, 0.1); }
.letter-spacing-wider { letter-spacing: 0.05em; }

.dropdown-item {
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
}

.dropdown-menu {
  border-radius: 12px;
  min-width: 200px;
}
</style>
